import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import {
  type QueryParams,
  invalidateQueriesAndReload,
  loadMore,
} from 'common/actions/inboxItemQueries';
import AJAX from 'common/AJAX';
import { type InboxItem, SortOptions as SortOptionNames } from 'common/api/resources/inbox';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import connect from 'common/core/connect';
import Dropdown from 'common/Dropdown';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Spinner from 'common/Spinner';
import isElementOnScreen from 'common/util/isElementOnScreen';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';

import AdminInboxItem from './AdminInboxItem';

import SpamBoxIcon from 'img/icons/spambox.svg';

import type { Dispatch } from 'redux-connect';

import 'css/components/subdomain/admin/AdminInbox/_AdminInboxItemList.scss';

type ItemList = {
  items: InboxItem[];
  loading: boolean;
  loadingMore: boolean;
  lastCreated?: Date;
  queryParams: QueryParams;
};

type ConnectProps = {
  invalidateQueriesAndReload: (queryParams: QueryParams) => void;
  loadMore: (itemList: ItemList) => void;
};

type OwnProps = {
  itemList: ItemList;
};

type Props = OwnProps & ConnectProps;

const SortOptions = {
  newest: {
    name: SortOptionNames.newest,
    render: 'Most recent',
  },
  oldest: {
    name: SortOptionNames.oldest,
    render: 'Oldest',
  },
};

const CommonErrors = {
  'not authorized': `You don't have the permissions take action on this item`,
};

const getItemKey = (itemType: 'comment' | 'post') => {
  switch (itemType) {
    case 'comment':
      return 'commentID';
    case 'post':
      return 'postID';
    default:
      throw new Error(`Invalid itemType, ${itemType}`);
  }
};

const AdminInboxItemList = ({ invalidateQueriesAndReload, itemList, loadMore }: Props) => {
  // props
  const { loading, items, queryParams } = itemList;

  // context
  const location = useContext(LocationContext);
  const router = useContext(RouterContext);

  // state
  const initialSort =
    location.query?.sort in SortOptionNames ? location.query.sort : SortOptions.newest.name;
  const [sort, setSort] = useState<SortOptionNames>(initialSort);

  // refs
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // helpers
  const onApproveItem = async (item: InboxItem) => {
    const response = await AJAX.post('/api/spam/unmark', {
      objectID: item._id,
      objectType: item.itemType,
    });
    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
      errors: {
        ...CommonErrors,
        'invalid commentID': 'Comment is invalid',
        'invalid postID': 'Post is invalid',
      },
    });

    if (error) {
      return { error: error.message };
    }

    await invalidateQueriesAndReload(queryParams);
    return { error: null };
  };

  const onDeleteItem = async (item: InboxItem) => {
    const { url, body } = {
      comment: { url: '/api/comments/delete', body: { commentID: item._id } },
      post: { url: '/api/posts/delete', body: { postID: item._id } },
    }[item.itemType];

    const response = await AJAX.post(url, body);
    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
      errors: CommonErrors,
    });

    if (error) {
      return { error: error.message };
    }

    await invalidateQueriesAndReload(queryParams);
    return { error: null };
  };

  const onSpamItem = async (item: InboxItem) => {
    const itemKey = getItemKey(item.itemType);
    const response = await AJAX.post('/api/spam/mark', {
      [itemKey]: item._id,
    });
    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
      errors: CommonErrors,
    });

    if (error) {
      return { error: error.message };
    }

    await invalidateQueriesAndReload(queryParams);
    return { error: null };
  };

  const onScroll = useCallback(() => {
    const loadMoreElement = loadMoreRef.current;
    if (itemList.loadingMore || !loadMoreElement) {
      return;
    }

    if (!isElementOnScreen(loadMoreElement, scrollRef.current)) {
      return;
    }

    loadMore(itemList);
  }, [itemList, loadMore]);

  const onSortChange = (name: SortOptionNames) => {
    setSort(name);

    router.replace({
      ...location,
      query: {
        ...location.query,
        sort: name,
      },
    });
  };

  // effects
  useEffect(() => {
    const scrollRefElement = scrollRef.current;
    scrollRefElement?.addEventListener('scroll', onScroll, false);

    return () => {
      scrollRefElement?.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  // renderers
  const renderItems = () => {
    if (loading) {
      return <Spinner />;
    }

    if (!items?.length) {
      return (
        <div className="emptyInbox">
          <div className="content">
            <LazyLoadedImage src={SpamBoxIcon} alt="Spambox icon" />
            <div className="noUnread">Moderation box is empty.</div>
          </div>
        </div>
      );
    }

    const itemText = items.length > 1 ? `items` : `item`;
    return (
      <div className="inboxContentContainer">
        <div className="inboxContent">
          <div className="inboxHeader">
            <div className="titleContainer">
              <h1 className="title">Moderation</h1>
              <div className="badge">Beta</div>
            </div>
            <p className="description">
              Review all incoming posts/comments that have been automatically flagged as spam.
            </p>
          </div>
          <div className="sectionDivider" />
          <div className="controls">
            <div className="itemCount">
              Showing {items.length} {itemText}
            </div>
            <div className="sortContainer">
              <div className="text">Sort by</div>
              <div className="dropdownContainer">
                <Dropdown
                  defaultSelectedName={sort}
                  onChange={onSortChange}
                  options={Object.values(SortOptions)}
                />
              </div>
            </div>
          </div>
          <div className="inboxItems">
            {items.map((item) => (
              <AdminInboxItem
                key={item._id}
                item={item}
                onApprove={onApproveItem}
                onDelete={onDeleteItem}
                onSpam={onSpamItem}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderPagination = () => {
    if (!itemList?.items || !itemList?.lastCreated) {
      return null;
    }

    if (itemList.loadingMore) {
      return (
        <div className="pagination">
          <Spinner />
        </div>
      );
    }

    return <div className="pagination" ref={loadMoreRef}></div>;
  };

  return (
    <div className="adminInboxItemList">
      <div className="adminInboxItemsContainer" ref={scrollRef}>
        {renderItems()}
        {renderPagination()}
      </div>
    </div>
  );
};

export default connect(null, (dispatch: Dispatch) => ({
  invalidateQueriesAndReload: (queryParams: QueryParams) =>
    dispatch(invalidateQueriesAndReload(queryParams)),
  loadMore: (itemList: ItemList) => dispatch(loadMore(itemList)),
}))(AdminInboxItemList) as unknown as React.FC<OwnProps>;
